import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { retrieveFromLocalStorage, toBase64, getFileExtension, sortArray } from '../../../utils/util';
import { NOT_SURE, ERROR } from '../../../utils/constants';

import {
  requestViewAllCategories,
  requestViewAllClassifications,
  requestViewAllTypes,
  requestViewMetricUnits,
  requestCreateProductListing,
  requestViewProductListing,
  requestUpdateProductListing,
  requestViewAllMetricUnits,
  requestCopyListing,
} from '../../../api/product/product.actions';
import { setCategories, setProductListing, setTypes } from '../../../slices/product/product.slice';
import { addSystemNotice } from '../../../slices/system/system.slice';

import UploadProductListing from '../../../components/screens/shop/upload-product-listing/UploadProductListing';

const UploadProductListingContainer = () => {
  const { classifications, categories, types, metricUnits, productListing } = useSelector(({ product }) => product);
  const currencyCode = useSelector(({ currency }) => currency.currencyCode);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { productExternalReference } = useParams();

  useEffect(() => {
    dispatch(requestViewAllClassifications());

    if (productExternalReference) {
      dispatch(requestViewProductListing({ productExternalReference }));
    }

    return () => dispatch(setProductListing(undefined));
  }, [dispatch, productExternalReference, currencyCode]);

  const retrieveCategories = useCallback(
    (classificationExternalReference) => {
      if (classificationExternalReference === NOT_SURE) {
        dispatch(setCategories([]));
        return;
      }

      dispatch(requestViewAllCategories({ classificationExternalReference }));
    },
    [dispatch]
  );

  const retrieveTypes = useCallback(
    (categoryExternalReference) => {
      if (categoryExternalReference === NOT_SURE) {
        dispatch(setTypes([]));
        return;
      }

      dispatch(requestViewAllTypes({ categoryExternalReference }));
    },
    [dispatch]
  );

  const retrieveMetricUnits = useCallback(
    (typeExternalReference) => {
      if (typeExternalReference === NOT_SURE) {
        dispatch(requestViewAllMetricUnits());
        return;
      }

      dispatch(requestViewMetricUnits({ typeExternalReference }));
    },
    [dispatch]
  );

  const showErrorToast = (message) => {
    dispatch(addSystemNotice({ type: ERROR, message }));
  };

  const mapImages = async (images) => {
    const mappedImages = [];

    for (const image of images) {
      mappedImages.push({
        base64String: await toBase64(image),
        documentExtension: getFileExtension(image.name),
      });
    }

    return mappedImages;
  };

  const createProductListing = async (productListing) => {
    productListing.photos = await mapImages(productListing.photos);
    productListing.personExternalReference = retrieveFromLocalStorage('personExternalReference');

    dispatch(requestCreateProductListing({ productListing, onSuccess: () => navigate('/shop/product-listings') }));
  };

  const updateProductListing = async (productListing) => {
    let newImages = productListing.photos.filter((photo) => typeof photo !== 'string');
    const oldImages = productListing.photos.filter((photo) => typeof photo === 'string');

    if (newImages.length > 0) {
      newImages = await mapImages(newImages);
      productListing.photos = newImages;
    } else {
      productListing.photos = [];
    }

    productListing.linkedMediaExternalReferences = oldImages;
    productListing.personExternalReference = retrieveFromLocalStorage('personExternalReference');
    productListing.productExternalReference = productExternalReference;

    dispatch(requestUpdateProductListing({ productListing, onSuccess: () => navigate('/shop/product-listings') }));
  };

  const copyListing = async (productListing) => {
    const copyListingRequest = {
      personExternalReference: retrieveFromLocalStorage('personExternalReference'),
      listingExternalReference: productExternalReference,
      statusString: productListing.status,
    };

    dispatch(requestCopyListing({ copyListingRequest, onSuccess: () => navigate('/shop/product-listings') }));
  };

  return (
    <UploadProductListing
      listing={productListing}
      classifications={sortArray(classifications)}
      categories={sortArray(categories)}
      types={sortArray(types)}
      metricUnits={metricUnits}
      retrieveCategories={retrieveCategories}
      retrieveTypes={retrieveTypes}
      retrieveMetricUnits={retrieveMetricUnits}
      createProductListing={createProductListing}
      updateProductListing={updateProductListing}
      copyListing={copyListing}
      showErrorToast={showErrorToast}
      currencyCode={currencyCode}
    />
  );
};

export default UploadProductListingContainer;
